import { getDeviceId, TRAFFIC_CONSENT_VERSION } from "./storage";
import { offlineSearch, offlineReverse, rememberOfflinePlaces } from "./offlinePlaces";
import type {
  LatLng,
  MapConfig,
  Place,
  ReportType,
  RoadReport,
  Route,
  RouteResponse,
  LocationFix,
  TrafficEstimate,
  PlaceCategory,
  NearbyPlaces,
  SearchConfig,
  CameraCatalog,
} from "./types";

export const apiBase = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const TIMEOUT_MS = 25_000;

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
  signal?: AbortSignal,
  timeoutMs = TIMEOUT_MS,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const cancel = () => controller.abort(signal?.reason);
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const response = await fetch(apiBase + path, {
      ...init,
      // Search, routing and report ownership depend on location/device identity.
      cache: "no-store",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        "X-Device-Id": getDeviceId(),
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
    if (response.status === 204 && response.ok) return undefined as T;
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const serverMessage =
        typeof body?.error === "string"
          ? body.error
          : typeof body?.message === "string"
            ? body.message
            : undefined;
      throw new ApiError(
        serverMessage || "השירות אינו זמין כרגע. נסו שוב בעוד רגע.",
        response.status,
      );
    }
    if (body === null)
      throw new ApiError("התקבלה תשובה לא תקינה מהשרת.", response.status);
    return body as T;
  } catch (error) {
    if (signal?.aborted) throw new DOMException("הבקשה בוטלה", "AbortError");
    if (timedOut)
      throw new ApiError("הבקשה ארכה יותר מדי זמן. בדקו את החיבור ונסו שוב.");
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      "לא ניתן להתחבר לשירות. בדקו את החיבור לאינטרנט ונסו שוב.",
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}

function positionParams(position: LatLng): Record<string, string> {
  if (
    !Number.isFinite(position.lat) ||
    !Number.isFinite(position.lng) ||
    Math.abs(position.lat) > 90 ||
    Math.abs(position.lng) > 180
  )
    throw new ApiError("המיקום אינו תקין.");
  return { lat: String(position.lat), lng: String(position.lng) };
}

function requireArray<T>(value: T[] | undefined): T[] {
  if (!Array.isArray(value)) throw new ApiError("התקבלה תשובה לא תקינה מהשרת.");
  return value;
}

export const api = {
  roadSpeeds: (point: LatLng, signal?: AbortSignal) => request<{roads: import("./speed").SpeedRoad[];fetchedAt:number}>(`/api/road-speeds?lat=${point.lat}&lng=${point.lng}`,{},signal,65_000),
  async webSearch(query: string, signal?: AbortSignal): Promise<Place[]> {
    const result = await request<{ places: Place[] }>(`/api/search/web?${new URLSearchParams({q:query.trim()})}`, {}, signal, 50_000);
    return requireArray(result.places);
  },
  async cameras(signal?: AbortSignal): Promise<CameraCatalog> {
    const result = await request<CameraCatalog>("/api/cameras", {}, signal);
    requireArray(result.cameras);
    return result;
  },
  searchConfig: () => request<SearchConfig>("/api/search/config"),
  async reverse(point: LatLng, signal?: AbortSignal):Promise<Place> {
    const params=new URLSearchParams(positionParams(point));
    try {
      if(navigator.onLine===false)throw new ApiError("אין חיבור לרשת");
      return await request<Place>(`/api/search/reverse?${params}`,{},signal,6_000);
    } catch(error) {
      if(signal?.aborted)throw error;
      const place=await offlineReverse(point);
      if(place)return place;
      throw error;
    }
  },
  resolveLocation: (input: string, signal?: AbortSignal) =>
    request<Place>(
      "/api/search/resolve",
      { method: "POST", body: JSON.stringify({ input }) },
      signal,
    ),
  async nearby(
    position: LatLng,
    category: PlaceCategory,
    signal?: AbortSignal,
  ): Promise<NearbyPlaces> {
    const params = new URLSearchParams({
      ...positionParams(position),
      category,
    });
    const result = await request<NearbyPlaces>(
      `/api/places/nearby?${params}`,
      {},
      signal,
      45_000,
    );
    requireArray(result.places);
    return result;
  },
  async search(
    query: string,
    position: LatLng,
    signal?: AbortSignal,
  ): Promise<Place[]> {
    if (query.trim().length < 2) return [];
    const params = new URLSearchParams({
      q: query.trim(),
      ...positionParams(position),
    });
    try {
      if (navigator.onLine === false) throw new ApiError("אין חיבור לרשת");
      const result = await request<{ places: Place[] }>(`/api/search?${params}`, {}, signal, 60_000);
      const places = requireArray(result.places);
      void rememberOfflinePlaces(places);
      return places;
    } catch (error) {
      if (signal?.aborted || error instanceof DOMException && error.name === "AbortError") throw error;
      if (error instanceof ApiError && error.status && error.status < 500) throw error;
        const places = await offlineSearch(query, position);
        if (places.length) return places;
        if (navigator.onLine === false) throw new ApiError("אין תוצאה במקומות שהורדו למכשיר. חיפוש באזור חדש דורש חיבור לשרתי Desty.");
        throw error;
    }
  },
  async route(
    from: LatLng,
    to: LatLng,
    signal?: AbortSignal,
    avoidDangers = false,
    mode: import("./transport").TravelMode = "car",
  ): Promise<Route[]> {
    positionParams(from);
    positionParams(to);
    const params = new URLSearchParams({
      fromLat: String(from.lat),
      fromLng: String(from.lng),
      toLat: String(to.lat),
      toLng: String(to.lng),
      avoidDangers: String(avoidDangers),
      mode,
    });
    const result = await request<{ routes: Route[] }>(
      `/api/route?${params}`,
      {},
      signal,
    );
    const routes = requireArray(result.routes);
    if (!routes.length)
      throw new ApiError("לא נמצא מסלול נסיעה בין המקומות האלה.");
    return routes;
  },
  async routeDetails(
    from: LatLng,
    to: LatLng,
    avoidDangers: boolean,
    signal?: AbortSignal,
    mode: import("./transport").TravelMode = "car",
  ): Promise<RouteResponse> {
    if (navigator.onLine === false) throw new ApiError("חישוב מסלול חדש דורש חיבור לשרתי Desty. המסלול שכבר נטען נשאר זמין.");
    positionParams(from);
    positionParams(to);
    const params = new URLSearchParams({
      fromLat: String(from.lat),
      fromLng: String(from.lng),
      toLat: String(to.lat),
      toLng: String(to.lng),
      avoidDangers: String(avoidDangers),
      mode,
    });
    const result = await request<RouteResponse>(
      `/api/route?${params}`,
      {},
      signal,
      75_000,
    );
    requireArray(result.routes);
    if (!result.routes.length) throw new ApiError("לא נמצא מסלול מתאים", 422);
    return result;
  },
  async reports(position: LatLng, signal?: AbortSignal): Promise<RoadReport[]> {
    const params = new URLSearchParams({
      ...positionParams(position),
      radius: "15000",
    });
    const result = await request<{ reports: RoadReport[] }>(
      `/api/reports?${params}`,
      {},
      signal,
    );
    return requireArray(result.reports);
  },
  async report(
    type: ReportType,
    position: LatLng,
    note: string,
    signal?: AbortSignal,
  ): Promise<RoadReport> {
    positionParams(position);
    const result = await request<RoadReport | { report: RoadReport }>(
      "/api/reports",
      {
        method: "POST",
        body: JSON.stringify({
          type,
          ...position,
          note: note.trim().slice(0, 240),
        }),
      },
      signal,
    );
    return "report" in result ? result.report : result;
  },
  async confirm(
    id: string,
    fix: LocationFix,
    signal?: AbortSignal,
  ): Promise<RoadReport> {
    const result = await request<RoadReport | { report: RoadReport }>(
      `/api/reports/${encodeURIComponent(id)}/confirm`,
      { method: "POST", body: JSON.stringify(fix) },
      signal,
    );
    return "report" in result ? result.report : result;
  },
  async resolve(
    id: string,
    fix: LocationFix,
    signal?: AbortSignal,
  ): Promise<void> {
    await request(
      `/api/reports/${encodeURIComponent(id)}/resolve`,
      {
        method: "POST",
        body: JSON.stringify(fix),
      },
      signal,
    );
  },
  async trafficSample(routeId: string, fix: LocationFix, signal?: AbortSignal) {
    return request<{ accepted: boolean; reason: string }>(
      "/api/traffic/samples",
      {
        method: "POST",
        body: JSON.stringify({
          ...fix,
          routeId,
          consent: true,
          navigationActive: true,
          hourlyConsentVersion: TRAFFIC_CONSENT_VERSION,
        }),
      },
      signal,
    );
  },
  async trafficEstimate(routeId: string, fix: LatLng, signal?: AbortSignal) {
    const params = new URLSearchParams({ ...positionParams(fix), routeId });
    return request<TrafficEstimate>(`/api/traffic/route?${params}`, {}, signal);
  },
  async removeReport(id: string): Promise<void> {
    await request<unknown>(`/api/reports/${encodeURIComponent(id)}/delete`, {
      method: "POST",
    });
  },
  async config(): Promise<MapConfig> {
    const config = await request<MapConfig>("/api/config");
    if (
      typeof config.tileUrl !== "string" ||
      typeof config.attribution !== "string"
    ) {
      throw new ApiError("הגדרות המפה אינן תקינות.");
    }
    return config;
  },
};
